import { computed, onScopeDispose, ref, watch } from 'vue';

// One bounded, authenticated request per conversation, shared by all visible
// email bubbles. No tracking-image URL is ever returned to the dashboard.
const subscriptions = new Map();
const POLL_MS = 30000;

function subscribe(accountId, conversationId, messageId) {
  const key = `${accountId}:${conversationId}`;
  let entry = subscriptions.get(key);
  if (!entry) {
    entry = {
      values: ref({}),
      ids: new Map(),
      timer: null,
      busy: false,
      revision: 0,
    };
    subscriptions.set(key, entry);
  }
  const schedule = (delay = 80) => {
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => entry.load(), delay);
  };
  entry.load = async () => {
    if (subscriptions.get(key) !== entry || entry.busy) return;
    if (document.hidden) {
      schedule(POLL_MS);
      return;
    }
    entry.busy = true;
    entry.controller = new AbortController();
    const revision = entry.revision;
    try {
      const ids = [...entry.ids.keys()];
      const values = {};
      const responses = await Promise.all(
        Array.from({ length: Math.ceil(ids.length / 100) }, (_, index) =>
          window.axios.get(
            `/api/v1/accounts/${accountId}/conversations/${conversationId}/email_open_signals`,
            {
              params: {
                message_ids: ids.slice(index * 100, index * 100 + 100),
              },
              signal: entry.controller.signal,
            }
          )
        )
      );
      responses.forEach(response => {
        Object.assign(values, response.data.signals || {});
      });
      if (subscriptions.get(key) === entry) entry.values.value = values;
    } catch {
      // An unavailable/unauthorized result must not look like "unread".
      if (subscriptions.get(key) === entry) entry.values.value = {};
    } finally {
      entry.busy = false;
      if (subscriptions.get(key) === entry) {
        schedule(revision === entry.revision ? POLL_MS : 80);
      }
    }
  };
  entry.ids.set(messageId, (entry.ids.get(messageId) || 0) + 1);
  entry.revision += 1;
  if (!entry.busy) schedule();
  let active = true;
  return {
    value: computed(() => entry.values.value[messageId] || null),
    stop: () => {
      if (!active) return;
      active = false;
      const remaining = entry.ids.get(messageId) - 1;
      if (remaining) entry.ids.set(messageId, remaining);
      else entry.ids.delete(messageId);
      entry.revision += 1;
      if (!entry.ids.size) {
        clearTimeout(entry.timer);
        entry.controller?.abort();
        subscriptions.delete(key);
      }
    },
  };
}

export function useEmailOpenSignals({
  accountId,
  conversationId,
  messageId,
  enabled,
}) {
  const signal = ref(null);
  let stopSubscription;
  let stopValue;
  watch(
    () => [
      accountId.value,
      conversationId.value,
      messageId.value,
      enabled.value,
    ],
    ([account, conversation, message, wanted]) => {
      stopValue?.();
      stopSubscription?.();
      signal.value = null;
      if (
        !wanted ||
        ![account, conversation, message].every(value =>
          /^[1-9]\d*$/.test(String(value))
        )
      )
        return;
      const subscription = subscribe(account, conversation, message);
      stopSubscription = subscription.stop;
      stopValue = watch(
        subscription.value,
        value => {
          signal.value = value;
        },
        { immediate: true }
      );
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    stopValue?.();
    stopSubscription?.();
  });
  return signal;
}
